<template>
  <AccordionCardInfoDisplay
    v-if="showForEntityTypes.includes(clickedEntityType) && currentEntity"
    :heading="title"
    :isExpanded="isExpanded"
    :contentHeight="!isLoading ? '240px' : undefined"
    @onClicked="handleClick"
  >
    <template #content>
      <div
        v-if="!isLoading && currentEntity.authorityData?.apartmentsInfo?.length"
        class="container"
      >
        <div
          v-for="(info, index) in currentEntity.authorityData?.apartmentsInfo"
          :key="index"
          class="accordion"
        >
          <p class="address-header">{{ info.address }}</p>
          <TableMini
            v-if="info.apartments.length"
            :items="getContentData(info.apartments)"
            widthHeading2="'fit-content'"
            widthHeading3="64px"
            :widthValue1="'55%'"
            :widthValue2="'fit-content'"
            :widthValue3="'60px'"
            :unlimitedLength="true"
            @onHovered="handleHover"
          >
            <template #heading1>
              {{ headers.heading1 }}
            </template>
            <template #heading2>{{ headers.heading2 }}</template>
            <template #heading3>{{ headers.heading3 }}</template>

            <template #value1="{ item }">
              <div
                class="table-cell"
                @mouseleave="showTooltip = false"
                @mouseenter="(e) => trackPointer(e, item.value1)"
              >
                <p>
                  {{ item.value1 }}
                </p>
                <TooltipStandard
                  v-show="showTooltip"
                  arrowPosition="top-center"
                  class="tooltip"
                  :pointerEvent="trackedMouseEvent"
                  :text="hoveredTitle"
                />
              </div>
            </template>
            <template #value2="{ item }">
              <span v-if="item">{{ item.value2 }}</span>
            </template>
            <template #value3="{ item }">
              <span v-if="item">{{ item.value3 }}</span>
            </template>
          </TableMini>
        </div>
      </div>
      <div
        v-else-if="!isLoading && !currentEntity.authorityData?.apartmentsInfo?.length"
        class="no-data"
      >
        <p>{{ t('general__data__no-data-published') }}</p>
      </div>
      <div v-else class="no-data">
        <PulseLoader :loading="true" color="#3F46D6" />
      </div>
    </template>
  </AccordionCardInfoDisplay>
</template>
<script lang="ts" setup>
import AccordionCardInfoDisplay from '@/components/features/info-display/AccordionCardInfoDisplay.vue'
import TableMini from '@/design-system/molecules/TableMini.vue'
import TooltipStandard from '@/design-system/atoms/TooltipStandard.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import { computed, ref } from 'vue'
import { Apartment, CoreEntityDto } from '@/interfaces/core.interface'
import { useI18n } from 'vue-i18n'
import { useSelectionStore } from '@/pinia/selection'
import { storeToRefs } from 'pinia'

const props = defineProps<{
  showForEntityTypes: string[]
  currentEntity: CoreEntityDto
  title: string
  isLoading: boolean
  expanded: boolean
}>()
const selectionStore = useSelectionStore()
const { t } = useI18n()
const isExpanded = ref(props.expanded)
const hoveredItem = ref(null)
const hoveredTitle = ref('')
const showTooltip = ref(false)
const trackedMouseEvent = ref({ x: 0, y: 0 })
const lastMousePosition = ref([0, 0])
const headers = computed(() => {
  return {
    heading1: t('general__data__apartment-number'),
    heading2: t('general__data__area-boa-short'),
    heading3: t('general__data__rooms')
  }
})
const currentEntity = computed(() => props.currentEntity)
const { clickedEntityType } = storeToRefs(selectionStore)

// methods
function getContentData(apartments: Apartment[]) {
  return apartments.map((apartment) => {
    return {
      value1: apartment.apartmentNumber ? apartment.apartmentNumber.toString() : '-',
      value2: apartment.livingSurface ? apartment.livingSurface.toString() : '-',
      value3: apartment.numberOfRooms ? apartment.numberOfRooms.toString() : '-'
    }
  })
}
function handleClick() {
  isExpanded.value = !isExpanded.value
}
function handleHover(item) {
  hoveredItem.value = item
}
function trackPointer(event, title) {
  hoveredTitle.value = title
  const rect = event.currentTarget.getBoundingClientRect()
  if (event) {
    if (
      lastMousePosition.value[0] === event.clientX &&
      lastMousePosition.value[1] === event.clientY
    )
      return
    lastMousePosition.value = [event.clientX, event.clientY]
    trackedMouseEvent.value = {
      x: rect.left + rect.width / 2,
      y: rect.bottom + rect.height / 2
    }
    const targetEl = event.target
    if (targetEl.firstElementChild) {
      showTooltip.value =
        targetEl.firstElementChild.clientWidth < targetEl.firstElementChild.scrollWidth
    }
  }
}
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/mixins.scss';
.container {
  height: 240px;
  overflow-x: hidden;
  overflow-y: auto;
  @include mixins.scrollbar-vertical;
  margin-top: 4px;
}
.accordion {
  flex: 1 1 auto;
  height: fit-content;
  z-index: 1;
  width: 100%;
  &:not(:first-child) {
    padding-top: 16px;
  }
  .extraText {
    color: variables.$color-greyscale-secondary;
  }
  .address-header {
    @include font-mixins.desktop-bold-paragraph;
    color: variables.$color-greyscale-content;
    margin: 0;
  }
  .table-cell {
    align-items: center;
    display: flex;
    height: 32px;
    width: 100%;
    p {
      color: variables.$color-greyscale-content;
      margin: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      @include font-mixins.desktop-regular-paragraph;
    }
  }
}
.no-data {
  width: 100%;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  p {
    @include font-mixins.desktop-regular-label;
  }
}
.tooltip {
  filter: drop-shadow(0px 0px 0.6px rgb(0, 0, 0));
  margin-top: -2px;
  :deep(.tooltip__arrow) {
    filter: unset;
  }
}
</style>
